"use client";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import * as React from "react";
import { createPortal } from "react-dom";

/* -------------------------------------------------------------------------------------------------
 * Improved tooltip system that tracks which specific tooltip should be visible
 * -----------------------------------------------------------------------------------------------*/

type TooltipContextValue = {
  activeId: string | null;
  position: { x: number; y: number } | null;
  setTooltip: (id: string | null, position?: { x: number; y: number }) => void;
};

const TooltipContext = React.createContext<TooltipContextValue | undefined>(
  undefined
);

function useTooltipContext(componentName: string): TooltipContextValue {
  const context = React.useContext(TooltipContext);
  if (!context) {
    throw new Error(
      `${componentName} must be used within a ClientTooltip component`
    );
  }
  return context;
}

/* -------------------------------------------------------------------------------------------------
 * ClientTooltip
 * -----------------------------------------------------------------------------------------------*/

export function ClientTooltip({ children }: { children: React.ReactNode }) {
  const [activeId, setActiveId] = React.useState<string | null>(null);
  const [position, setPosition] = React.useState<{
    x: number;
    y: number;
  } | null>(null);

  const setTooltip = React.useCallback(
    (id: string | null, newPosition?: { x: number; y: number }) => {
      setActiveId(id);
      if (newPosition) {
        setPosition(newPosition);
      } else if (id === null) {
        setPosition(null);
      }
    },
    []
  );

  return (
    <TooltipContext.Provider value={{ activeId, position, setTooltip }}>
      {children}
    </TooltipContext.Provider>
  );
}

/* -------------------------------------------------------------------------------------------------
 * TooltipTrigger
 * -----------------------------------------------------------------------------------------------*/

interface TooltipTriggerProps {
  children: React.ReactNode;
  id: string;
}

export function TooltipTrigger({ children, id }: TooltipTriggerProps) {
  const { setTooltip } = useTooltipContext("TooltipTrigger");
  const triggerRef = React.useRef<SVGGElement | null>(null);
  const isMobile = useMediaQuery("(max-width: 640px)");

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        setTooltip(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, [setTooltip]);

  return (
    <g
      ref={triggerRef}
      onPointerMove={(event) => {
        // Only handle mouse events, not touch
        if (event.pointerType === "mouse") {
          setTooltip(id, { x: event.clientX, y: event.clientY });
        }
      }}
      onPointerLeave={() => {
        setTooltip(null);
      }}
      onTouchStart={(event) => {
        // On mobile, trigger when tapped instead of hover
        setTooltip(id, {
          x: event.touches[0].clientX,
          y: event.touches[0].clientY - (isMobile ? 50 : 10), // Give more space on mobile
        });

        // Auto-hide after a delay on mobile
        setTimeout(() => {
          setTooltip(null);
        }, 2000);
      }}
    >
      {children}
    </g>
  );
}

/* -------------------------------------------------------------------------------------------------
 * TooltipContent
 * -----------------------------------------------------------------------------------------------*/

interface TooltipContentProps {
  children: React.ReactNode;
  id: string;
  className?: string;
}

export function TooltipContent({
  children,
  id,
  className,
}: TooltipContentProps) {
  const { activeId, position } = useTooltipContext("TooltipContent");
  const tooltipRef = React.useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery("(max-width: 640px)");

  // Only render on client-side
  const [isMounted, setIsMounted] = React.useState(false);

  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  // Calculate position based on viewport
  const getTooltipPosition = () => {
    if (!tooltipRef.current || !position) return {};

    const tooltipWidth = tooltipRef.current.offsetWidth;
    const tooltipHeight = tooltipRef.current.offsetHeight;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Check if tooltip would go off-screen
    const willOverflowRight = position.x + tooltipWidth + 10 > viewportWidth;
    const willOverflowBottom = position.y + tooltipHeight + 10 > viewportHeight;

    // Calculate optimal position
    let left = willOverflowRight
      ? position.x - tooltipWidth - 10
      : position.x + 10;

    let top = willOverflowBottom
      ? position.y - tooltipHeight - 10
      : position.y + 10;

    // Ensure tooltip stays on screen
    left = Math.max(10, Math.min(viewportWidth - tooltipWidth - 10, left));
    top = Math.max(10, Math.min(viewportHeight - tooltipHeight - 10, top));

    return { top, left };
  };

  // Only show this tooltip if it's the active one
  if (id !== activeId || !position || !isMounted) {
    return null;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className={`border-base-200 bg-base-100 fixed z-50 w-fit rounded-lg border p-0 shadow-lg dark:border-zinc-800 dark:bg-zinc-900 ${className || ""}`}
      style={
        isMobile
          ? {
              top: position.y - 20,
              left: Math.min(position.x + 20, window.innerWidth - 250),
              maxWidth: "230px",
            }
          : getTooltipPosition()
      }
    >
      {children}
    </div>,
    document.body
  );
}
